























































































































































import axios from "axios";
import { getGeoEntityByISO3 } from "@/models/GeoEntity.ts";
import { Vue, Component, Watch, Prop } from "vue-property-decorator";
import { AgGridVue } from "ag-grid-vue";
import { columnDefs } from "@/tabular_datasets/tabular_data_columns";
import Excel from "@/models/Excel.ts";
import TabularDataset from "@/models/TabularDataset.ts";
import TabularDatasetRow from "@/models/TabularDatasetRow.ts";
import DatePicker from "@/scaffold/GridDatePicker.ts";

@Component({
  components: { AgGridVue },
  filters: {
    getName(value: any) {
      return { name: "", ...value }.name;
    },
  },
})
export default class TabularDataGrid extends Vue {
  @Prop() id?: number;
  private components = { datePicker: DatePicker };
  private columnDefs = columnDefs;
  private gridOptions: any = {};
  private tabularDataset: TabularDataset = new TabularDataset();
  private paddingRowsCount: number = 100;
  private showForm: boolean = false;

  debug() {
    this.tabularDataset.debug();
  }

  // Vue Hooks
  // -------------------------------------------------------------------
  async created() {
    if (this.id) {
      this.tabularDataset = await TabularDataset.load(this.id);
      this.tabularDataset.addEmptyRows(this.paddingRowsCount);
    }
  }

  // Computed
  // -------------------------------------------------------------------
  get allColumns() {
    // @ts-ignore
    return this.gridOptions.columnApi.getAllColumns();
  }

  get isLocked() {
    return this.tabularDataset.isLocked();
  }

  // Methods
  // -------------------------------------------------------------------

  // Can't make it a computed value since they are cached
  // and columns are not part of the model so it won't get updated
  visibaleColumns() {
    let columns = this.allColumns;
    columns = columns.filter((column: any) => column.visible);
    return columns;
  }

  // Import/Export dataset
  // -------------------------------------------------------------------
  async loadDataset() {
    this.gridOptions.api.showLoadingOverlay();
    const id = this.tabularDataset.id;
    if (id) {
      this.tabularDataset = await TabularDataset.load(id);
      this.tabularDataset.addEmptyRows(this.paddingRowsCount);
    }
    this.hideEmptyColumns();
    this.gridOptions.api.hideOverlay();
  }

  async save(lock: boolean = false) {
    this.gridOptions.api.showLoadingOverlay();
    try {
      const response = await this.tabularDataset.save(lock);
      console.log(response);
      const id = this.tabularDataset.id;
      if (id) {
        this.tabularDataset = await TabularDataset.load(id);
        this.tabularDataset.addEmptyRows(this.paddingRowsCount);
      }
      this.$Notice.success({
        title: "Saved successfully",
        desc: `${response.data._inserted} inserted, ${
          response.data._updated
        } updated, ${response.data._deleted} deleted, ${
          response.data._locked
        } locked`,
      });
    } catch (error) {
      console.log(error.response);
      this.$Notice.error({
        title: "Failed to save",
        desc: error.data,
      });
    }

    this.gridOptions.api.hideOverlay();
  }

  notice(action: string, n: number) {
    if (n > 0) {
      this.$Notice.success({
        desc: `${n} entries were successfully ${action}`,
      });
    } else if (n < 0) {
      this.$Notice.error({
        desc: `${-n} entries failed to be ${action}`,
      });
    }
  }

  async updateComments() {
    try {
      await this.tabularDataset.updateComments();
      this.$Notice.success({
        desc: "Comments updated seccessfully",
      });
    } catch (error) {
      this.$Notice.error({
        desc: "Comments failed to be updated",
      });
    }
  }

  // importExcel imports a file at the end of the dataset and pad the bottom with empty rows
  async importExcel(event: any) {
    if (this.isLocked) return;
    this.gridOptions.api.showLoadingOverlay();
    let file = event.target.files[0];
    let data: any[] = await Excel.readFile(file, ["start_date", "end_date"]);
    // Make sure lon/lat are numeric
    for (const row of data) {
      row.org_lat = parseFloat(row.org_lat);
      row.org_lon = parseFloat(row.org_lon);
      row.dst_lat = parseFloat(row.dst_lat);
      row.dst_lon = parseFloat(row.dst_lon);
    }
    this.tabularDataset.concat(data);
    this.tabularDataset.addEmptyRows(this.paddingRowsCount);
    this.hideEmptyColumns();
    this.gridOptions.api.hideOverlay();
    event.target.value = "";
  }

  // exportExcel exports the data grid into an excel with the dataset name as file name
  exportExcel(full: boolean) {
    if (this.isLocked) return;
    let data: any[] = [];
    let filename = `${this.tabularDataset.name}_template.xlsx`;
    if (full) {
      data = this.tabularDataset.dataset;
      filename = `${this.tabularDataset.name}_records.xlsx`;
    }
    const excelFile = new Excel();
    const headers = this.visibaleColumns()
      .filter((column: any) => !column.colDef.checkboxSelection)
      .map((column: any) => column.colDef.headerName);
    excelFile.addSheet("Dataset", headers, headers, data).download(filename);
  }

  // Manage data grid
  // -------------------------------------------------------------------

  showAllColumns() {
    const api = this.gridOptions.columnApi;
    const columns = this.allColumns;
    for (let column of columns) {
      const field = column.colDef.field;
      if (!["id", "parent_id", "iso3"].includes(field)) {
        api.setColumnVisible(field, true);
      }
    }
  }

  hideEmptyColumns() {
    // @ts-ignore
    const api = this.gridOptions.columnApi;
    const columns = this.allColumns;
    // hide others if empty
    for (let column of columns) {
      const field = column.colDef.field;
      api.setColumnVisible(field, this.tabularDataset.isColumnEmpty(field));
    }
    // always hide these
    api.setColumnVisible("id", false);
    api.setColumnVisible("parent_id", false);
    api.setColumnVisible("iso3", false);
    this.gridOptions.api.refreshHeader();
    this.gridOptions.api.sizeColumnsToFit();
  }

  clear() {
    if (this.isLocked) return;
    this.tabularDataset.clear();
    this.tabularDataset.addEmptyRows(this.paddingRowsCount);
    this.showAllColumns();
  }

  getRowStyle(params: any) {
    const row: TabularDatasetRow = params.data;
    switch (row.action) {
      case "INSERT":
        return { backgroundColor: "#f6f6eb" };
      case "DELETE":
        return { backgroundColor: "#f6eeeb" };
      case "UPDATE":
        return { backgroundColor: "#ebf6f6" };
      default:
        return { backgroundColor: "#ffffff" };
    }
  }

  redrawRows() {
    this.gridOptions.api.redrawRows();
  }

  onCellValueChanged(params: any) {
    if (this.isLocked) return;
    const row: TabularDatasetRow = params.data;
    row.setUpdated();
    this.gridOptions.api.updateRowData({ update: [row] });
  }

  onGridReady(params: any) {
    // @ts-ignore
    this.gridOptions.api.closeToolPanel();
    // window.gridOptions = this.gridOptions;
  }

  getContextMenuItems(params: any) {
    return [
      {
        name: "Delete",
        action: () => {
          const rows = params.api.getSelectedRows();
          for (const row of rows) {
            row.setDeleted();
          }
          this.tabularDataset.removeDeleted();
          params.api.deselectAll();
          params.api.clearRangeSelection();
          params.api.refreshHeader();
        },
      },
      "separator",
      ...params.defaultItems,
    ];
  }

  // geolocation
  // -------------------------------------------------------------------

  async geolocate() {
    if (this.isLocked) return;

    const rows = this.tabularDataset.dataset;
    this.gridOptions.api.showLoadingOverlay();

    const total = rows.length;
    let resolved = 0;
    let failed = 0;
    let skipped = 0;

    // Lookup org
    for (const row of rows) {
      if (!!row.org_lon && !!row.org_lat) {
        skipped++;
        continue;
      }
      const iso3 = row.iso3 || "";
      const iso = getGeoEntityByISO3(iso3).iso;

      if (row.org_name && row.org_name.trim() !== "") {
        const name = row.org_name;
        if (iso && name) {
          try {
            let loc = await this.osmnamesGeocode(iso, name);
            row.org_lat = loc.lat;
            row.org_lon = loc.lon;
            row.setUpdated();
            resolved++;
          } catch (error) {
            failed++;
            console.error(error);
          }
        }
      }
    }

    this.$Notice.info({
      title: "Geolocation Origin",
      desc: `Total: ${total}<br>resolved: ${resolved}<br>failed: ${failed}<br>skipped: ${skipped}`,
      duration: 20,
    });

    resolved = 0;
    failed = 0;
    skipped = 0;

    // Lookup dst
    for (const row of rows) {
      if (!!row.dst_lon && !!row.dst_lat) {
        skipped++;
        continue;
      }
      const iso3 = row.iso3 || "";
      const iso = getGeoEntityByISO3(iso3).iso;

      if (row.dst_name && row.dst_name.trim() !== "") {
        const name = row.dst_name;
        if (iso && name) {
          try {
            let loc = await this.osmnamesGeocode(iso, name);
            row.dst_lat = loc.lat;
            row.dst_lon = loc.lon;
            row.setUpdated();
            resolved++;
          } catch (error) {
            failed++;
            console.error(error);
          }
        }
      }
    }

    this.$Notice.info({
      title: "Geolocation Destination",
      desc: `Total: ${total}<br>resolved: ${resolved}<br>failed: ${failed}<br>skipped: ${skipped}`,
      duration: 20,
    });

    // Grid redraw
    this.gridOptions.api.hideOverlay();
    this.redrawRows();
  }

  async osmnamesGeocode(iso: string, name: string) {
    const url = `https://osmnames.idmcdb.org/${iso}/q/${name}.js`;
    const response = await axios.get(url);
    const result = response.data.results[0];
    return { lat: result.lat, lon: result.lon };
  }

  async googleGeoCode(iso: string, name: string) {
    const url = `https://maps.googleapis.com/maps/api/geocode/json`;
    const response = await axios.get(url, {
      params: {
        address: `${name}+${iso}`,
        key: `AIzaSyB251OTWfMluH17WoniiNzXe_4D23p3Xpo`,
      },
    });
    const result = response.data.results[0];
    return {
      lat: result.geometry.location.lat,
      lon: result.geometry.location.lng,
    };
  }
}
